import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Edit, MapPin, Info, Package, Trash2, RotateCcw, Wallet, Pencil, Save, X } from 'lucide-react';

const AutomatDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API, user } = useContext(AuthContext);
  const [automat, setAutomat] = useState(null);
  const [standort, setStandort] = useState(null);
  const [befuellungen, setBefuellungen] = useState([]);
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetReason, setResetReason] = useState('');
  const [formData, setFormData] = useState({});
  
  // State für Füllmengen-Bearbeitung
  const [editingSlot, setEditingSlot] = useState(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [savingQuantity, setSavingQuantity] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [automatRes, befuellungenRes] = await Promise.all([
        axios.get(`${API}/automaten/${id}`),
        axios.get(`${API}/befuellungen/automat/${id}`)
      ]);
      
      setAutomat(automatRes.data);
      setBefuellungen(befuellungenRes.data);
      
      // Fetch standort NUR FÜR ADMINS - Helfer dürfen keine Standortdaten sehen
      if (user?.role === 'admin' && automatRes.data.current_location_id) {
        try {
          const standortRes = await axios.get(`${API}/standorte/${automatRes.data.current_location_id}`);
          setStandort(standortRes.data);
        } catch (err) {
          console.error('Error loading standort:', err);
        }
      }
      
      // Fetch financials
      try {
        const finRes = await axios.get(`${API}/automaten/${id}/financials`);
        setFinancials(finRes.data);
      } catch (err) {
        console.error('Error loading financials:', err);
        setFinancials({ has_data: false });
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Füllmenge bearbeiten - Start
  const handleEditQuantity = (slotNumber, currentQuantity) => {
    setEditingSlot(slotNumber);
    setEditQuantity(currentQuantity.toString());
  };

  // Füllmenge bearbeiten - Abbrechen
  const handleCancelEditQuantity = () => {
    setEditingSlot(null);
    setEditQuantity('');
  };

  // Füllmenge bearbeiten - Speichern
  const handleSaveQuantity = async (slotNumber) => {
    const newQuantity = parseInt(editQuantity, 10);
    
    // Validierung
    if (isNaN(newQuantity) || newQuantity < 0) {
      toast.error('Bitte eine gültige Zahl >= 0 eingeben');
      return;
    }
    
    setSavingQuantity(true);
    try {
      const response = await axios.patch(`${API}/automaten/${id}/fill-quantity`, {
        slot_number: slotNumber,
        new_quantity: newQuantity
      });
      
      toast.success(`Füllmenge aktualisiert: ${response.data.old_quantity} → ${response.data.new_quantity}`);
      
      // Daten neu laden um alle Werte zu aktualisieren
      await fetchData();
      
      // Edit-Modus beenden
      setEditingSlot(null);
      setEditQuantity('');
    } catch (error) {
      console.error('Fehler beim Speichern der Füllmenge:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Speichern';
      toast.error(errorMsg);
    } finally {
      setSavingQuantity(false);
    }
  };

  const handleEdit = () => {
    setFormData({
      code: automat.code,
      machine_type: automat.machine_type,
      coin_type: automat.coin_type,
      status: automat.status,
      is_defect: automat.is_defect || false,
      defect_note: automat.defect_note || '',
      purchase_price: automat.purchase_price || null,
      purchase_date: automat.purchase_date || '',
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    // Validate defect_note if is_defect is true
    if (formData.is_defect && (!formData.defect_note || formData.defect_note.trim().length < 1)) {
      toast.error('Defektbeschreibung ist erforderlich (mindestens 1 Zeichen)');
      return;
    }
    
    // Prüfe ob Automat im Lager ist (hier im Handler statt global)
    const isAutomatInStorage = !automat?.current_location_id;
    
    try {
      let dataToSend;
      
      // Helfer dürfen nur bestimmte Felder senden
      if (user?.role === 'helper') {
        dataToSend = {
          is_defect: formData.is_defect,
          defect_note: formData.is_defect ? formData.defect_note : null,
        };
        
        // Wenn im Lager: auch Münztyp und Automatennummer senden
        if (isAutomatInStorage) {
          dataToSend.coin_type = formData.coin_type;
          dataToSend.code = formData.code?.trim();
        }
      } else {
        // Admin sendet alle Felder
        dataToSend = {
          ...formData,
          purchase_price: formData.purchase_price === '' ? null : formData.purchase_price,
          defect_note: formData.is_defect ? formData.defect_note : null,
        };
      }
      
      await axios.put(`${API}/automaten/${id}`, dataToSend);
      toast.success(formData.is_defect ? 'Defekt gesetzt!' : 'Automat aktualisiert!');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      const status = error.response?.status;
      const errorMsg = error.response?.data?.detail || 'Fehler beim Aktualisieren';
      
      // Spezifische Fehlermeldungen je nach Status
      if (status === 403) {
        toast.error(`Keine Berechtigung: ${errorMsg}`);
      } else if (status === 409) {
        // Duplikat-Fehler für Automatennummer
        if (errorMsg === 'AUTOMAT_NUMBER_EXISTS') {
          toast.error('Automatennummer bereits vergeben. Bitte wählen Sie eine andere Nummer.');
        } else {
          toast.error(errorMsg);
        }
      } else if (status === 422) {
        toast.error(`Ungültige Eingabe: ${errorMsg}`);
      } else {
        toast.error(errorMsg);
      }
      console.error('[AutomatDetail] Update error:', error);
    }
  };

  const handleResetBefuellung = async () => {
    if (!resetReason || resetReason.trim().length < 5) {
      toast.error('Grund muss mindestens 5 Zeichen lang sein');
      return;
    }

    try {
      const latestBefuellung = befuellungen[0];
      if (!latestBefuellung) {
        toast.error('Keine Befüllung zum Zurücksetzen gefunden');
        return;
      }

      await axios.delete(`${API}/befuellungen/${latestBefuellung.id}`, {
        params: { deleted_reason: resetReason }
      });
      
      toast.success('Befüllung zurückgesetzt!');
      setShowResetDialog(false);
      setResetReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
    }
  };

  // Loading- und Null-Checks VOR allen Automat-Zugriffen
  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  if (!automat) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Automat nicht gefunden</p>
        <Button onClick={() => navigate('/automaten')} className="mt-4">
          Zurück zur Übersicht
        </Button>
      </div>
    );
  }

  // Diese Variablen MÜSSEN nach dem null-Check definiert werden
  const isInStorage = !automat.current_location_id;
  const canResetBefuellung = automat.status === 'Lager VOLL' && befuellungen.length > 0;

  return (
    <div className="space-y-6 animate-slide-in pb-24 sm:pb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate('/automaten')} size="sm" className="sm:size-default">
            <ArrowLeft size={18} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Zurück</span>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              {automat.code}
            </h1>
            <p className="text-slate-600 text-sm sm:text-base">{automat.machine_type}</p>
          </div>
        </div>
        {['admin', 'helper'].includes(user?.role) && (
          <div className="flex gap-2 flex-wrap">
            {/* Diese Aktionen nur für Admin */}
            {user?.role === 'admin' && automat.status === 'Lager LEER' && (
              <Button 
                onClick={() => navigate(`/befuellung/${automat.id}`)} 
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700"
              >
                <Package size={20} />
                <span>Befüllen</span>
              </Button>
            )}
            {user?.role === 'admin' && automat.status === 'AUSZÄHLEN' && (
              <Button 
                onClick={() => navigate(`/automaten/auszaehlen-id/${automat.id}`)} 
                className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700"
              >
                <Wallet size={20} />
                <span>Auszählen</span>
              </Button>
            )}
            {user?.role === 'admin' && automat.status === 'Lager VOLL' && (
              <Button 
                onClick={() => navigate(`/automaten/auszaehlen-id/${automat.id}`)} 
                className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700"
              >
                <Wallet size={20} />
                <span>Auszählen</span>
              </Button>
            )}
            {user?.role === 'admin' && canResetBefuellung && (
              <Button 
                onClick={() => setShowResetDialog(true)} 
                variant="outline"
                className="flex items-center space-x-2 border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                <RotateCcw size={20} />
                <span>Befüllung zurücksetzen</span>
              </Button>
            )}
            {/* Bearbeiten-Button für Admin und Helfer */}
            <Button onClick={handleEdit} className="flex items-center space-x-2" size="sm">
              <Edit size={18} />
              <span className="hidden sm:inline">Bearbeiten</span>
              <span className="sm:hidden">Edit</span>
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Basic Info */}
        <Card className="p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold mb-4 flex items-center">
            <Info className="mr-2 text-blue-600" size={20} />
            Grundinformationen
          </h2>
          <div className="space-y-3 text-sm sm:text-base">
            <div className="flex justify-between">
              <span className="text-slate-600">Automat-Nr.:</span>
              <span className="font-medium">{automat.code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Typ:</span>
              <span className="font-medium">{automat.machine_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Münztyp:</span>
              <span className="font-medium">{automat.coin_type} €</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Status:</span>
              <span className={`font-medium ${
                automat.status === 'AKTIV' ? 'text-green-600' :
                automat.status === 'Lager VOLL' ? 'text-blue-600' :
                automat.status === 'Lager LEER' ? 'text-orange-600' :
                automat.status === 'AUSZÄHLEN' ? 'text-purple-600' :
                'text-red-600'
              }`}>
                {automat.status}
              </span>
            </div>
            {automat.is_defect && (
              <div className="pt-3 border-t">
                <p className="text-red-600 font-medium">⚠️ Defekt</p>
                {automat.defect_note && (
                  <p className="text-sm text-slate-600 mt-1">{automat.defect_note}</p>
                )}
              </div>
            )}
            {/* Vergangene Defektbeschreibung (behoben) - nur anzeigen wenn nicht mehr defekt aber Note vorhanden */}
            {!automat.is_defect && automat.defect_note && (
              <div className="pt-3 border-t">
                <p className="text-slate-500 font-medium">📋 Vergangener Defekt (behoben):</p>
                <p className="text-sm text-slate-400 mt-1">{automat.defect_note}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Location Info - NUR FÜR ADMINS SICHTBAR */}
        {user?.role === 'admin' && (
          <Card className="p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold mb-4 flex items-center">
              <MapPin className="mr-2 text-blue-600" size={20} />
              Standort
            </h2>
            {standort ? (
              <div className="space-y-3 text-sm sm:text-base">
                <div className="flex justify-between">
                  <span className="text-slate-600">Name:</span>
                  <span className="font-medium">{standort.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Code:</span>
                  <span className="font-medium">{standort.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Stadt:</span>
                  <span className="font-medium">{standort.city}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Straße:</span>
                  <span className="font-medium">{standort.street}</span>
                </div>
                <div className="pt-3">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/standorte/${standort.id}`)}
                    className="w-full"
                  >
                    Standort Details
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-slate-500">Aktuell keinem Standort zugeordnet</p>
            )}
          </Card>
        )}
      </div>

      {/* Aktuelle Befüllung - nur für Admin wenn befüllt */}
      {user?.role === 'admin' && automat?.current_fill && Object.keys(automat.current_fill).length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Package className="mr-2 text-green-600" />
            Aktuelle Befüllung
          </h2>
          <div className="space-y-3">
            {Object.entries(automat.current_fill).map(([slotKey, fillData]) => {
              const slotNumber = parseInt(slotKey, 10);
              const isEditing = editingSlot === slotNumber;
              
              return (
                <div 
                  key={slotKey} 
                  className="flex justify-between items-center p-3 bg-green-50 border border-green-200 rounded-lg"
                >
                  <div className="flex-1">
                    <span className="font-medium text-green-800">Schacht {slotKey}</span>
                    <span className="text-sm text-green-600 ml-3">
                      {fillData.product_name || 'Unbekanntes Produkt'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <Input
                          type="number"
                          min="0"
                          value={editQuantity}
                          onChange={(e) => setEditQuantity(e.target.value)}
                          className="w-20 h-8 text-center"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveQuantity(slotNumber);
                            if (e.key === 'Escape') handleCancelEditQuantity();
                          }}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSaveQuantity(slotNumber)}
                          disabled={savingQuantity}
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-100"
                        >
                          <Save size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEditQuantity}
                          disabled={savingQuantity}
                          className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                        >
                          <X size={16} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="font-bold text-green-700 min-w-[40px] text-right">
                          {fillData.quantity} Stk
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditQuantity(slotNumber, fillData.quantity)}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                          title="Füllmenge bearbeiten"
                        >
                          <Pencil size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Klicken Sie auf das Stift-Symbol, um die Füllmenge zu ändern.
          </p>
        </Card>
      )}

      {/* Befüllungs-Historie */}
      {user?.role === 'admin' && befuellungen.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Package className="mr-2 text-blue-600" />
            Befüllungs-Historie ({befuellungen.length})
          </h2>
          <div className="space-y-3">
            {befuellungen.slice(0, 3).map((befuellung) => (
              <div key={befuellung.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <div>
                  <span className="font-medium">{befuellung.type}</span>
                  <span className="text-sm text-slate-500 ml-3">
                    {new Date(befuellung.filled_at).toLocaleDateString('de-DE')}
                  </span>
                </div>
                <span className="text-sm text-slate-600">
                  {befuellung.positions?.length || 0} Position(en)
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Financials */}
      {financials?.has_data && user?.role === 'admin' && (
        <Card className="p-6 bg-gradient-to-br from-green-50 to-blue-50">
          <h2 className="text-xl font-bold mb-4">💰 Finanzstatistik</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-slate-500 mb-1">Gesamtitems</p>
              <p className="text-2xl font-bold">{financials.total_items || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-slate-500 mb-1">Erwarteter Umsatz</p>
              <p className="text-2xl font-bold text-green-600">
                {(financials.expected_revenue || 0).toFixed(2)} €
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-slate-500 mb-1">Erwarteter Gewinn</p>
              <p className="text-2xl font-bold text-blue-600">
                {(financials.expected_profit || 0).toFixed(2)} €
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-slate-500 mb-1">Marge</p>
              <p className="text-2xl font-bold text-purple-600">
                {(financials.margin_percentage || 0).toFixed(1)}%
              </p>
            </div>
          </div>
          {financials.last_fill_date && (
            <p className="text-xs text-slate-500 mt-4">
              Letzte Befüllung: {new Date(financials.last_fill_date).toLocaleString('de-DE')}
            </p>
          )}
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {user?.role === 'helper' 
                ? (isInStorage ? 'Automat bearbeiten (Lager)' : 'Defekt bearbeiten')
                : 'Automat bearbeiten'
              }
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            {/* Automatennummer - Admin immer, Helfer nur im Lager */}
            {(user?.role === 'admin' || (user?.role === 'helper' && isInStorage)) && (
              <div>
                <Label>Automatennummer</Label>
                <Input
                  value={formData.code || ''}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="z.B. A-001"
                  disabled={user?.role === 'helper' && !isInStorage}
                />
                {user?.role === 'helper' && !isInStorage && (
                  <p className="text-xs text-orange-600 mt-1">
                    Automatennummer kann nur im Lager geändert werden.
                  </p>
                )}
              </div>
            )}
            
            {/* Admin-Only Felder */}
            {user?.role === 'admin' && (
              <div>
                <Label>Typ</Label>
                <Select
                  value={formData.machine_type}
                  onValueChange={(value) => setFormData({ ...formData, machine_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Standard">Standard</SelectItem>
                    <SelectItem value="Goliath">Goliath</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Münztyp - Admin immer, Helfer nur im Lager */}
            <div>
              <Label>Münztyp</Label>
              <Select
                value={formData.coin_type}
                onValueChange={(value) => setFormData({ ...formData, coin_type: value })}
                disabled={user?.role === 'helper' && !isInStorage}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0,20">0,20 €</SelectItem>
                  <SelectItem value="0,50">0,50 €</SelectItem>
                  <SelectItem value="1,00">1,00 €</SelectItem>
                  <SelectItem value="2,00">2,00 €</SelectItem>
                </SelectContent>
              </Select>
              {user?.role === 'helper' && !isInStorage && (
                <p className="text-xs text-orange-600 mt-1">
                  Münztyp kann nur im Lager geändert werden.
                </p>
              )}
            </div>
            
            {/* Status - Nur für Admin */}
            {user?.role === 'admin' && (
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                  disabled={formData.is_defect}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Lager LEER">Lager LEER</SelectItem>
                    <SelectItem value="Lager VOLL">Lager VOLL</SelectItem>
                    <SelectItem value="AKTIV">AKTIV</SelectItem>
                    <SelectItem value="AUSZÄHLEN">AUSZÄHLEN</SelectItem>
                  </SelectContent>
                </Select>
                {formData.is_defect && (
                  <p className="text-xs text-slate-500 mt-1">
                    Status ist gesperrt, wenn Defekt aktiv ist
                  </p>
                )}
              </div>
            )}
            
            {/* Defekt-Felder für Admin UND Helfer */}
            <div className="flex items-center space-x-2">
              <Switch
                id="defect-switch"
                checked={formData.is_defect}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  is_defect: checked,
                  defect_note: checked ? formData.defect_note : ''
                })}
              />
              <Label htmlFor="defect-switch">Defekt</Label>
            </div>
            {formData.is_defect && (
              <div>
                <Label>Defektbeschreibung *</Label>
                <Textarea
                  value={formData.defect_note || ''}
                  onChange={(e) => setFormData({ ...formData, defect_note: e.target.value })}
                  rows={3}
                  placeholder="Beschreiben Sie den Defekt..."
                  required
                  minLength={1}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Pflichtfeld (mindestens 1 Zeichen)
                </p>
              </div>
            )}
            <Button type="submit" className="w-full">
              Speichern
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Befüllung Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Befüllung zurücksetzen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Möchten Sie die Befüllung dieses Automaten zurücksetzen? Der Automat wird auf &quot;Lager LEER&quot; gesetzt und die Produktbestände werden wiederhergestellt.
            </p>
            <div>
              <Label>Grund für Zurücksetzen (mindestens 5 Zeichen)</Label>
              <Textarea
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                rows={3}
                placeholder="z.B. Fehlerhafte Befüllung, Automat defekt..."
                required
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowResetDialog(false)}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleResetBefuellung}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                Zurücksetzen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AutomatDetailPage;